<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AutomationSelect from './AutomationSelect.vue';

const props = defineProps({
  triggerType: { type: String, default: 'card_created' },
  triggerConfig: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:triggerType', 'update:triggerConfig']);

const { t } = useI18n();

const STALLED_TRIGGERS = [
  'card_stalled_no_move',
  'card_stalled_no_status',
  'card_stalled_no_interaction',
];
const DATE_TRIGGERS = ['card_closing_date', 'task_due_date', 'meeting_date'];
const STATUS_TRIGGERS = ['card_status_changed', 'meeting_status_changed'];

const triggerOptions = computed(() => [
  {
    value: 'card_created',
    label: t('KANBAN.AUTOMATIONS.TRIGGERS.CARD_CREATED'),
  },
  { value: 'card_moved', label: t('KANBAN.AUTOMATIONS.TRIGGERS.CARD_MOVED') },
  {
    value: 'card_status_changed',
    label: t('KANBAN.AUTOMATIONS.TRIGGERS.CARD_STATUS_CHANGED'),
  },
  { value: 'tag_added', label: t('KANBAN.AUTOMATIONS.TRIGGERS.TAG_ADDED') },
  { value: 'tag_removed', label: t('KANBAN.AUTOMATIONS.TRIGGERS.TAG_REMOVED') },
  {
    value: 'card_stalled_no_move',
    label: t('KANBAN.AUTOMATIONS.TRIGGERS.CARD_STALLED_NO_MOVE'),
  },
  {
    value: 'card_stalled_no_status',
    label: t('KANBAN.AUTOMATIONS.TRIGGERS.CARD_STALLED_NO_STATUS'),
  },
  {
    value: 'card_stalled_no_interaction',
    label: t('KANBAN.AUTOMATIONS.TRIGGERS.CARD_STALLED_NO_INTERACTION'),
  },
  {
    value: 'card_closing_date',
    label: t('KANBAN.AUTOMATIONS.TRIGGERS.CARD_CLOSING_DATE'),
  },
  {
    value: 'task_completed',
    label: t('KANBAN.AUTOMATIONS.TRIGGERS.TASK_COMPLETED'),
  },
  {
    value: 'task_due_date',
    label: t('KANBAN.AUTOMATIONS.TRIGGERS.TASK_DUE_DATE'),
  },
  {
    value: 'meeting_scheduled',
    label: t('KANBAN.AUTOMATIONS.TRIGGERS.MEETING_SCHEDULED'),
  },
  {
    value: 'meeting_status_changed',
    label: t('KANBAN.AUTOMATIONS.TRIGGERS.MEETING_STATUS_CHANGED'),
  },
  {
    value: 'meeting_date',
    label: t('KANBAN.AUTOMATIONS.TRIGGERS.MEETING_DATE'),
  },
]);

const showHours = computed(() => STALLED_TRIGGERS.includes(props.triggerType));
const showDateOffset = computed(() =>
  DATE_TRIGGERS.includes(props.triggerType)
);
const showStatusTo = computed(() =>
  STATUS_TRIGGERS.includes(props.triggerType)
);

const statusOptions = computed(() => {
  const keys =
    props.triggerType === 'meeting_status_changed'
      ? ['scheduled', 'done', 'no_show', 'rescheduled', 'cancelled']
      : ['open', 'won', 'lost'];
  return keys.map(k => ({ value: k, label: k }));
});

const dateModeOptions = computed(() => [
  { value: 'days_before', label: t('KANBAN.AUTOMATIONS.DAYS_BEFORE') },
  { value: 'on_day', label: t('KANBAN.AUTOMATIONS.ON_DAY') },
  { value: 'days_after', label: t('KANBAN.AUTOMATIONS.DAYS_AFTER') },
]);

function setType(value) {
  emit('update:triggerType', value);
  emit('update:triggerConfig', {});
}

function updateConfig(key, value) {
  emit('update:triggerConfig', { ...props.triggerConfig, [key]: value });
}

const statusTo = computed(() => props.triggerConfig.to ?? '');
const dateMode = computed(() => {
  if (props.triggerConfig.days_after != null) return 'days_after';
  if (props.triggerConfig.on_day) return 'on_day';
  return 'days_before';
});

function setDateMode(mode) {
  if (mode === 'on_day') {
    emit('update:triggerConfig', { on_day: true });
  } else {
    emit('update:triggerConfig', {
      [mode]: props.triggerConfig.days_before || 1,
    });
  }
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <span class="text-xs font-semibold uppercase text-n-slate-10">
      {{ $t('KANBAN.AUTOMATIONS.WHEN') }}
    </span>

    <AutomationSelect
      :model-value="triggerType"
      :options="triggerOptions"
      :placeholder="$t('KANBAN.AUTOMATIONS.WHEN')"
      @update:model-value="setType"
    />

    <div v-if="showHours" class="flex items-center gap-2">
      <input
        type="number"
        min="1"
        :value="triggerConfig.hours"
        class="w-24 rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
        @input="updateConfig('hours', Number($event.target.value))"
      />
      <span class="text-sm text-n-slate-11">{{
        $t('KANBAN.AUTOMATIONS.HOURS')
      }}</span>
    </div>

    <div v-if="showStatusTo" class="flex items-center gap-2">
      <span class="text-sm text-n-slate-11">{{
        $t('KANBAN.AUTOMATIONS.STATUS_TO')
      }}</span>
      <AutomationSelect
        :model-value="statusTo"
        :options="statusOptions"
        clearable
        :placeholder="$t('KANBAN.AUTOMATIONS.ANY')"
        @update:model-value="updateConfig('to', $event === null ? '' : $event)"
      />
    </div>

    <div v-if="showDateOffset" class="flex items-center gap-2 flex-wrap">
      <AutomationSelect
        :model-value="dateMode"
        :options="dateModeOptions"
        :placeholder="$t('KANBAN.AUTOMATIONS.DAYS_BEFORE')"
        @update:model-value="setDateMode"
      />
      <input
        v-if="dateMode !== 'on_day'"
        type="number"
        min="0"
        :value="triggerConfig[dateMode]"
        class="w-20 rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
        @input="updateConfig(dateMode, Number($event.target.value))"
      />
      <span v-if="dateMode !== 'on_day'" class="text-sm text-n-slate-11">
        {{ $t('KANBAN.AUTOMATIONS.DAYS') }}
      </span>
    </div>
  </div>
</template>
